/**
 * Единственные форматтеры чисел, денег, количеств и дат (аудит U14).
 * Локальные fmt/num/rub в страницах переводятся сюда при миграции страницы.
 *
 *   formatNumber(1164)            → «1 164»      (разряды неразрывным пробелом)
 *   formatNumber(0.3, 1)          → «0,3»        (запятая, как принято)
 *   formatMoney(1500)             → «1 500 ₽»
 *   formatMoney(1500.5, { kopecks: true }) → «1 500,50 ₽»
 *   formatQty(5)                  → «5 шт»
 *   formatQty(3, ['короб','короба','коробов']) → «3 короба»
 *   formatPercent(0.168)          → «16,8 %»  (доля 0..1)
 *   plural / pluralN              — склонения, из utils/plural (единственная реализация)
 *   formatDate / formatDateTime / formatTime — по Москве, из utils/time
 *
 * Пустое значение (null/undefined/'' /NaN) → «—» (или opts.empty).
 */
import { plural, pluralN } from './plural';
import { fmtDate, fmtDateTime, fmtTime, fmtShortDateTime } from './time';

export { plural, pluralN };

const NBSP = ' ';
const isEmpty = (v) => v === null || v === undefined || v === '' || (typeof v === 'number' && Number.isNaN(v));
const toNum = (v) => (typeof v === 'number' ? v : Number(String(v).replace(/\s/g, '').replace(',', '.')));

/** Число с разрядами: maxFraction — сколько знаков после запятой максимум (по умолчанию 0). */
export function formatNumber(value, maxFraction = 0, { empty = '—', minFraction = 0, sign = false } = {}) {
  if (isEmpty(value)) return empty;
  const n = toNum(value);
  if (!Number.isFinite(n)) return empty;
  const s = n.toLocaleString('ru-RU', {
    minimumFractionDigits: Math.min(minFraction, maxFraction),
    maximumFractionDigits: maxFraction,
  }).replace(/ /g, NBSP);
  return sign && n > 0 ? `+${s}` : s;
}

/** Деньги: «1 500 ₽». kopecks: true — всегда 2 знака; 'auto' — копейки, только если они есть. */
export function formatMoney(value, { kopecks = 'auto', empty = '—', currency = '₽', sign = false } = {}) {
  if (isEmpty(value)) return empty;
  const n = toNum(value);
  if (!Number.isFinite(n)) return empty;
  const hasKop = Math.round(Math.abs(n) * 100) % 100 !== 0;
  const digits = kopecks === true ? 2 : (kopecks === 'auto' && hasKop ? 2 : 0);
  const s = formatNumber(n, digits, { minFraction: digits, sign });
  return currency ? `${s}${NBSP}${currency}` : s;
}

/**
 * Количество: «5 шт» или со склонением «3 короба».
 * unit — строка («шт», «кг») или три формы для plural.
 */
export function formatQty(value, unit = 'шт', { empty = '—', maxFraction = 0 } = {}) {
  if (isEmpty(value)) return empty;
  const n = toNum(value);
  if (!Number.isFinite(n)) return empty;
  const word = Array.isArray(unit) ? plural(n, unit) : unit;
  return word ? `${formatNumber(n, maxFraction)}${NBSP}${word}` : formatNumber(n, maxFraction);
}

/** Доля 0..1 → «16,8 %». asFraction=false — если value уже в процентах (16.8). */
export function formatPercent(value, { digits = 1, asFraction = true, empty = '—' } = {}) {
  if (isEmpty(value)) return empty;
  const n = toNum(value);
  if (!Number.isFinite(n)) return empty;
  return `${formatNumber(asFraction ? n * 100 : n, digits)}${NBSP}%`;
}

/** 01.09.2026 по Москве; пусто → «—» */
export const formatDate = (v, empty = '—') => (v ? fmtDate(v) || empty : empty);
/** 01.09.2026 21:57 по Москве */
export const formatDateTime = (v, empty = '—') => (v ? fmtDateTime(v) || empty : empty);
/** 21:57 по Москве */
export const formatTime = (v, empty = '—') => (v ? fmtTime(v) || empty : empty);
/** 01.09 21:57 — без года */
export const formatShortDateTime = (v, empty = '—') => (v ? fmtShortDateTime(v) || empty : empty);
